import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Share2, Check, Sparkles, Globe, UtensilsCrossed, Smartphone, Loader2,
  Clock, BadgePercent, ArrowRight, Tag, MapPin, Coins, Store,
} from "lucide-react";
import { GPS } from "@/constants/testIds";
import { compressImage } from "@/flics/PicComposer";
import { logoFor } from "@/lib/brandLogos";
import { useMarketRewards } from "@/lib/useMarketRewards";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// 🛒 POG MARKET teaser — shown when this merchant sells rewards for BUCKS.
const MarketTeaser = ({ businessId }) => {
  const byBiz = useMarketRewards();
  const m = byBiz.get(businessId);
  if (!m) return null;
  return (
    <button data-testid="deals-panel-market-teaser" onClick={() => window.location.assign("/market")}
      className="mt-3 w-full flex items-center gap-2.5 rounded-xl bg-amber-500/10 border border-amber-400/40 px-3.5 py-2.5 text-left hover:bg-amber-500/20 transition-colors">
      <span className="text-lg">🛒</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-heading font-black text-amber-300">SPEND YOUR POG BUCKS HERE</span>
        <span className="block text-[11px] font-bold text-slate-300">{m.n} reward{m.n === 1 ? "" : "s"} in the POG MARKET · from 🪙 {m.min}</span>
      </span>
      <span className="shrink-0 text-[10px] font-black tracking-wider text-amber-400/80">MARKET →</span>
    </button>
  );
};

// ⭐ Average deal rating — social proof from real redemptions.
const RatingChip = ({ businessId }) => {
  const [sum, setSum] = useState(null);
  useEffect(() => {
    if (!businessId) return;
    axios.get(`${BACKEND_URL}/api/deal-reviews/summary/${encodeURIComponent(businessId)}`)
      .then((r) => setSum(r.data)).catch(() => {});
  }, [businessId]);
  if (!sum || !sum.count) return null;
  return (
    <span data-testid="deal-rating-chip" className="inline-flex items-center gap-1 text-xs font-extrabold text-amber-300 border border-amber-400/40 bg-amber-500/10 rounded-full px-3 py-1 mt-2">
      ⭐ {sum.avg} · {sum.count} POG'R rating{sum.count === 1 ? "" : "s"}
    </span>
  );
};

// ✅/⚠️ Community deal-quality votes — keeps scouted deals trustworthy.
const DealVotes = ({ spot, driver }) => {
  const [votes, setVotes] = useState({ active: 0, expired: 0, mine: null });
  const cast = (vote) => {
    if (!driver) { toast("Sign in to vote on deals, POG'R! 🎈"); return; }
    axios.post(`${BACKEND_URL}/api/spotlights/${spot.id}/vote`, { vote }, { withCredentials: true })
      .then((r) => {
        setVotes({ active: r.data.active_votes, expired: r.data.expired_votes, mine: vote });
        const plus = r.data.bucks ? " +1 POG BUCK! 🎯" : "";
        toast.success((vote === "active" ? "Confirmed live — thanks POG'R! ✅" : r.data.retired ? "Deal retired — the scout will re-check the site! 🫡" : "Flagged — a few more flags retires it! ⚠️") + plus);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Vote failed — try again!"));
  };
  return (
    <div className="flex items-center gap-2 mt-2.5" data-testid="deal-votes">
      <button data-testid="deal-vote-active" onClick={() => cast("active")}
        className={`h-8 px-3 rounded-full text-[10px] font-black border transition-colors active:scale-95 ${votes.mine === "active" ? "bg-emerald-500 text-slate-900 border-emerald-400" : "bg-slate-800 text-emerald-300 border-emerald-400/40"}`}>
        ✅ STILL ACTIVE{votes.active > 0 ? ` · ${votes.active}` : ""}
      </button>
      <button data-testid="deal-vote-expired" onClick={() => cast("expired")}
        className={`h-8 px-3 rounded-full text-[10px] font-black border transition-colors active:scale-95 ${votes.mine === "expired" ? "bg-rose-500 text-white border-rose-400" : "bg-slate-800 text-rose-300 border-rose-400/40"}`}>
        ⚠️ EXPIRED?{votes.expired > 0 ? ` · ${votes.expired}` : ""}
      </button>
    </div>
  );
};

// 🕵️ POG'R DEAL SPOTTING — share a merchant's publicly advertised deal (+10 POG BUCKS).
const DealSpotForm = ({ businessId, driver, biz }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!driver) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/deal-spots`,
        { business_id: businessId, text, source_url: url }, { withCredentials: true });
      let msg = r.data.message;
      if (photo) {
        try {
          const blob = await compressImage(photo);
          const fd = new FormData();
          fd.append("file", blob, "pic.jpg");
          fd.append("caption", text.trim().slice(0, 80));
          fd.append("business_id", businessId || "");
          if (biz?.lat != null) { fd.append("lat", biz.lat); fd.append("lng", biz.lng); }
          await axios.post(`${BACKEND_URL}/api/flics`, fd, { withCredentials: true });
          msg += " 📸 Pic posted to POG PICS & FLICS!";
        } catch (e) { /* deal still spotted */ }
      }
      toast(msg);
      setOpen(false); setText(""); setUrl(""); setPhoto(null);
    } catch (e) { toast(e.response?.data?.detail || "Couldn't post — try again"); }
    setBusy(false);
  };
  return (
    <div className="mt-3">
      {!open ? (
        <button data-testid="spot-a-deal-btn" onClick={() => setOpen(true)}
          className="w-full h-10 rounded-xl border border-dashed border-amber-400/50 text-amber-300 text-sm font-heading font-bold hover:bg-amber-400/10 transition-colors">
          🕵️ Seen a deal advertised here? SPOT IT → +10 POG BUCKS
        </button>
      ) : (
        <div className="rounded-xl border border-amber-400/40 p-3 space-y-2 bg-slate-900/60">
          <input data-testid="spot-deal-text" value={text} onChange={(e) => setText(e.target.value)} maxLength={140}
            placeholder='e.g. "Medium coffee $1.50 all week" (as publicly advertised)'
            className="w-full h-10 rounded-lg bg-slate-800 border border-slate-700 px-3 text-sm text-white placeholder:text-slate-500" />
          <input data-testid="spot-deal-url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="Source link (optional)"
            className="w-full h-9 rounded-lg bg-slate-800 border border-slate-700 px-3 text-xs text-white placeholder:text-slate-500" />
          <label data-testid="spot-deal-photo-label" className={`flex items-center gap-2 h-9 rounded-lg border border-dashed px-3 cursor-pointer text-xs font-bold ${photo ? "border-emerald-400/60 text-emerald-300 bg-emerald-500/10" : "border-slate-600 text-slate-400 hover:border-amber-400/50"}`}>
            📸 {photo ? `Pic attached: ${photo.name.slice(0, 24)}` : "Snap a pic — posts to POG PICS & FLICS 🔥 (optional)"}
            <input data-testid="spot-deal-photo" type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
          </label>
          <p className="text-[10px] text-slate-500">Only deals the merchant publicly advertises. Posted with a "not affiliated" disclaimer.</p>
          <div className="flex gap-2">
            <button data-testid="spot-deal-submit" onClick={submit} disabled={busy || text.trim().length < 8}
              className="flex-1 h-9 rounded-lg bg-amber-400 text-slate-900 text-sm font-heading font-black disabled:opacity-40">
              {busy ? "…" : "POST IT 🎈"}
            </button>
            <button onClick={() => setOpen(false)} className="h-9 px-3 rounded-lg bg-slate-800 text-slate-400 text-sm">✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

// 💰 DEAL STACK carousel — rotating merchant deal cards, a new one every 5s (Sir Eric's dots mockup).
const DealStackCarousel = ({ businessId }) => {
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setCards([]); setIdx(0);
    if (!businessId) return;
    axios.get(`${BACKEND_URL}/api/deal-stack/${encodeURIComponent(businessId)}`)
      .then((r) => setCards(r.data.cards || [])).catch(() => {});
  }, [businessId]);
  useEffect(() => {
    if (cards.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % cards.length), 5000);
    return () => clearInterval(t);
  }, [cards.length]);
  if (!cards.length) return null;
  const c = cards[Math.min(idx, cards.length - 1)];
  const [emoji, label] = { deal: ["🔥", "DEAL"], sale: ["🏷️", "SALE"], discount: ["💸", "DISCOUNT"], ad: ["📣", "AD"] }[c.kind] || ["🔥", "DEAL"];
  return (
    <div data-testid="deal-stack-carousel" className="mt-3">
      <div key={c.id} className="relative rounded-2xl p-5 bg-slate-900/70 border-2 border-fuchsia-400/70 shadow-[0_0_28px_rgba(232,121,249,0.3)] fade-in">
        {c.discount && (
          <div className="absolute -top-3 -right-2 w-16 h-16 rounded-full bg-gradient-to-br from-fuchsia-300 to-fuchsia-500 text-slate-900 flex flex-col items-center justify-center font-heading font-black leading-none text-center shadow-lg rotate-6">
            <span className="text-[15px]">{String(c.discount).split(" ")[0]}</span>
            <span className="text-[9px] tracking-wide">{String(c.discount).split(" ").slice(1).join(" ") || "OFF"}</span>
          </div>
        )}
        <p className="text-[10px] font-black tracking-[0.2em] uppercase text-fuchsia-300">{emoji} Merchant {label} · straight from the shop</p>
        <p data-testid="deal-stack-title" className="font-heading font-extrabold text-xl leading-tight pr-14 text-white mt-1">{c.title}</p>
        {c.detail && <p className="text-slate-300 text-sm mt-1">{c.detail}</p>}
        <p className="text-[11px] mt-2 flex items-center gap-1 text-fuchsia-300 font-semibold"><Clock className="w-3 h-3" />Live thru {String(c.expires_at || "").slice(0, 10)}</p>
      </div>
      {cards.length > 1 && (
        <div data-testid="deal-stack-dots" className="flex justify-center gap-1.5 mt-2.5">
          {cards.map((x, i) => (
            <button key={x.id} data-testid={`deal-stack-dot-${i}`} onClick={() => setIdx(i)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${i === idx ? "bg-fuchsia-400" : "bg-slate-600 hover:bg-slate-500"}`} />
          ))}
        </div>
      )}
    </div>
  );
};

// 📡 POG PING — Phase 1 of the POG RUN NETWORK: ask the merchant for a delivery/meet-up.
const PogPingRow = ({ ads, driver }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("meet"); // meet | runner | guaranteed
  const [fee, setFee] = useState(50);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const geo = () => new Promise((res) => {
    if (!navigator.geolocation) return res(null);
    navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { timeout: 5000 });
  });
  const biz = ads.business || {};
  const pickGuaranteed = async () => {
    if (mode === "guaranteed") { setMode("meet"); setQuote(null); return; }
    if (!biz.lat || !biz.lng) { toast.error("This spot has no map pin — GUARANTEED needs one!"); return; }
    setMode("guaranteed"); setQuote({ loading: true });
    const pos = await geo();
    if (!pos) { toast.error("Location needed to price a GUARANTEED run 📍"); setQuote({ error: true }); return; }
    try {
      const r = await axios.post(`${BACKEND_URL}/api/pings/guaranteed/quote`,
        { pickup_lat: biz.lat, pickup_lng: biz.lng, drop_lat: pos.lat, drop_lng: pos.lng }, { withCredentials: true });
      setQuote({ ...r.data, pos });
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't price that run!"); setQuote({ error: true }); }
  };
  const send = async () => {
    setBusy(true);
    if (mode === "guaranteed" && quote?.pos) {
      try {
        const r = await axios.post(`${BACKEND_URL}/api/pings/guaranteed/checkout`, {
          business_id: ads.business_id, business_name: ads.business_name || "", note,
          pickup_lat: biz.lat, pickup_lng: biz.lng, lat: quote.pos.lat, lng: quote.pos.lng,
          origin_url: window.location.origin,
        }, { withCredentials: true });
        window.location.href = r.data.url;
        return;
      } catch (e) { toast.error(e.response?.data?.detail || "Checkout didn't start — try again!"); setBusy(false); return; }
    }
    const pos = await geo();
    try {
      const r = await axios.post(`${BACKEND_URL}/api/pings`, {
        business_id: ads.business_id, business_name: ads.business_name || "",
        deal_title: "", note, lat: pos?.lat, lng: pos?.lng, delivery: mode === "runner", fee,
      }, { withCredentials: true });
      toast.success(r.data.message);
      setSent(true); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Ping didn't land — try again!"); }
    setBusy(false);
  };
  if (sent) {
    return (
      <a href="/pings" data-testid="pog-ping-sent" className="mt-3 block w-full text-center rounded-2xl bg-sky-500/10 border border-sky-400/40 px-4 py-2.5 text-[11px] font-black text-sky-300">
        📡 PING FIRED — track it in MY PINGS →
      </a>
    );
  }
  return (
    <div className="mt-3">
      {!open ? (
        <button data-testid="pog-ping-btn"
          onClick={() => { if (!driver) { toast("Sign in to fire a PING, POG'R! 📡"); return; } setOpen(true); }}
          className="w-full rounded-2xl bg-gradient-to-r from-sky-500/15 to-cyan-500/15 border border-sky-400/40 hover:from-sky-500/25 hover:to-cyan-500/25 transition-colors px-4 py-3 flex items-center gap-3 text-left">
          <span className="text-lg">📡</span>
          <span className="flex-1">
            <span className="block font-heading font-bold text-sm text-sky-100">POG PING — ask for a meet-up or delivery</span>
            <span className="block text-[11px] text-slate-400">Signal the merchant · they set a POG RENDEZVOUS point 🤝</span>
          </span>
        </button>
      ) : (
        <div className="rounded-2xl bg-slate-900/80 border border-sky-400/30 p-3" data-testid="pog-ping-form">
          <input data-testid="pog-ping-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
            placeholder="Optional note — 'Can you meet me at the park entrance?'"
            className="w-full h-10 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm font-semibold px-3 outline-none focus:border-sky-400/60" />
          <div className="flex items-center gap-2 mt-2">
            <button data-testid="pog-ping-delivery" onClick={() => { setMode((m) => (m === "runner" ? "meet" : "runner")); setQuote(null); }}
              className={`h-9 px-3 rounded-xl border text-[11px] font-bold transition-colors ${mode === "runner" ? "bg-emerald-500/20 border-emerald-400/70 text-emerald-200" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
              🛵 RUNNER
            </button>
            <button data-testid="pog-ping-guaranteed" onClick={pickGuaranteed}
              className={`h-9 px-3 rounded-xl border text-[11px] font-bold transition-colors ${mode === "guaranteed" ? "bg-amber-500/20 border-amber-400/70 text-amber-200" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
              💳 GUARANTEED
            </button>
            {mode === "runner" && (
              <select data-testid="pog-ping-fee" value={fee} onChange={(e) => setFee(+e.target.value)}
                className="h-9 flex-1 rounded-xl bg-slate-800 border border-emerald-400/40 text-emerald-200 text-[11px] font-bold px-2 outline-none">
                {[25, 50, 100, 150, 200].map((f) => <option key={f} value={f}>{f} 🎯 run fee (runner keeps {Math.floor(f * 0.75)})</option>)}
              </select>
            )}
          </div>
          {mode === "guaranteed" && (
            <div data-testid="pog-ping-guar-quote" className="mt-2 rounded-xl bg-amber-500/10 border border-amber-400/40 px-3 py-2">
              {quote?.loading ? (
                <p className="text-[11px] font-bold text-amber-200">📡 Pricing your GUARANTEED run…</p>
              ) : quote?.error ? (
                <button data-testid="pog-ping-guar-retry" onClick={() => { setMode("meet"); setQuote(null); pickGuaranteed(); }}
                  className="text-[11px] font-black text-rose-300 underline">Quote failed — tap to retry 🔁</button>
              ) : quote?.price ? (
                <p className="text-[11px] font-bold text-amber-200">
                  💳 <b>{quote.price}</b> — paid by card. Runner or pro courier, your delivery is GUARANTEED. Full refund if declined.
                </p>
              ) : null}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <button data-testid="pog-ping-send" onClick={send} disabled={busy || (mode === "guaranteed" && !quote?.price)}
              className="flex-1 h-10 rounded-xl bg-sky-500 text-slate-900 font-heading font-black text-sm disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : mode === "guaranteed" ? `PAY ${quote?.price || ""} & FIRE 💳` : "FIRE THE PING 📡"}
            </button>
            <button onClick={() => setOpen(false)} className="h-10 px-3 rounded-xl bg-slate-800 text-slate-400 text-sm">✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Ads / Sales panel — the double-tap deals sheet (extracted from GpsOverlay, Map Cleanup Stage 4).
export const DealsPanel = ({
  ads, onClose, shareDeal, copied, openLink, adsLoading, startRedeem, redeemBusy,
  routeTo, checkIn, checkinBusy, checkinMsg, driver, userPos, distMiles,
  showVisitQr, visitQrBusy, navigate, inviteBusiness, spotlight: spotlightProp,
}) => {
  const [spotFB, setSpotFB] = useState(null);
  useEffect(() => {
    setSpotFB(null);
    if (ads?.business_id && !spotlightProp) {
      // 🎈 backstop: green DEALS chip promised a deal — fetch it directly for this business
      axios.get(`${BACKEND_URL}/api/businesses/${encodeURIComponent(ads.business_id)}/spotlight`)
        .then((r) => setSpotFB(r.data?.spotlight || null)).catch(() => {});
    }
  }, [ads?.business_id, spotlightProp]);
  const spotlight = spotlightProp || spotFB;
  const brandImg = ads ? logoFor({ name: ads.business_name, logo_url: null, links: {} }, spotlight, BACKEND_URL) : null;
  return (
  <AnimatePresence>
    {ads && (
      <motion.div
        className="absolute inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          data-testid={GPS.adsPanel}
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong rounded-3xl w-full max-w-md p-6 relative max-h-[88vh] overflow-y-auto overscroll-contain no-scrollbar"
        >
          <button data-testid={GPS.adsPanelClose} onClick={onClose} className="absolute top-4 right-4 w-12 h-12 rounded-xl hover:bg-slate-700/60 flex items-center justify-center">
            <X className="w-5 h-5 text-slate-300" />
          </button>
          <button
            data-testid="share-deal-btn"
            onClick={() => shareDeal(ads.business)}
            className="absolute top-4 right-16 h-9 px-3 rounded-xl bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 flex items-center gap-1.5 text-xs font-bold transition-colors"
            title="Share this deal"
          >
            {copied ? <><Check className="w-4 h-4 text-emerald-400" /> Copied</> : <><Share2 className="w-4 h-4" /> Share</>}
          </button>
          <div className="flex flex-col items-center text-center mb-4 pt-1">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold tracking-[0.2em] uppercase text-rose-300 border border-rose-400/40 bg-rose-500/10 rounded-full px-3 py-1 shadow-[0_0_18px_rgba(244,63,94,0.35)]">
              <Sparkles className="w-3.5 h-3.5" /> Sales &amp; Offers
            </span>
            <div className="flex items-center justify-center gap-3 mt-4 w-full">
              <div className="relative w-14 h-14 rounded-full bg-slate-800/80 border border-slate-600/60 flex items-center justify-center overflow-hidden shrink-0">
                <span className="font-heading font-black text-sky-300 text-base">POG</span>
                {brandImg && <img src={brandImg} alt="" className="absolute inset-0 w-full h-full object-contain p-1.5 bg-white" onError={(e) => { e.currentTarget.remove(); }} />}
                <img src={`${BACKEND_URL}/api/logos/${ads.business_id}`} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
              <h2 className="font-heading text-2xl font-extrabold leading-tight max-w-[54%]">{ads.business_name}</h2>
          <button
            data-testid="deals-panel-flics-btn"
            onClick={() => window.location.assign(`/flics?biz=${encodeURIComponent(ads.business_id || "")}&bizname=${encodeURIComponent(ads.business_name || "")}&blat=${ads.business?.lat ?? ""}&blng=${ads.business?.lng ?? ""}`)}
            className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-orange-500/15 border border-orange-400/50 text-orange-300 text-[11px] font-black active:scale-95 transition-transform"
          >
            🎞 PICS &amp; FLICS
          </button>
              <img src="/pogzilla.png" alt="" className="w-20 h-20 object-contain shrink-0 drop-shadow-[0_0_14px_rgba(52,211,153,0.6)]" />
            </div>
            {ads.business && !ads.business.is_sale && (
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-emerald-300 border border-emerald-400/40 bg-emerald-500/10 rounded-full px-3 py-1 mt-3">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Open now
              </span>
            )}
            <RatingChip businessId={ads.business_id} />
            <MarketTeaser businessId={ads.business_id} />
          </div>

          {ads.links && Object.keys(ads.links).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5" data-testid="biz-links">
              {ads.links.website && (
                <button data-testid="biz-link-website" onClick={() => openLink(ads.business_id, "website", ads.links.website)}
                  className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-slate-800/70 border border-slate-700/60 text-sm font-semibold text-sky-300 hover:bg-slate-700/70 transition-colors">
                  <Globe className="w-4 h-4" /> Website
                </button>
              )}
              {ads.links.order_url && (
                <button data-testid="biz-link-order" onClick={() => openLink(ads.business_id, "order", ads.links.order_url)}
                  className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/25 transition-colors">
                  <UtensilsCrossed className="w-4 h-4" /> Order Now
                </button>
              )}
              {ads.links.ios_app && (
                <button data-testid="biz-link-ios" onClick={() => openLink(ads.business_id, "ios", ads.links.ios_app)}
                  className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-slate-800/70 border border-slate-700/60 text-sm font-semibold text-slate-200 hover:bg-slate-700/70 transition-colors">
                  <Smartphone className="w-4 h-4" /> App Store
                </button>
              )}
              {ads.links.android_app && (
                <button data-testid="biz-link-android" onClick={() => openLink(ads.business_id, "android", ads.links.android_app)}
                  className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-slate-800/70 border border-slate-700/60 text-sm font-semibold text-slate-200 hover:bg-slate-700/70 transition-colors">
                  <Smartphone className="w-4 h-4" /> Google Play
                </button>
              )}
            </div>
          )}

          {adsLoading || ads.loading ? (
            <div className="flex items-center gap-3 py-8 justify-center text-slate-300">
              <Loader2 className="w-5 h-5 animate-spin text-rose-400" /> Loading offers…
            </div>
          ) : ads.ads && ads.ads.length > 0 ? (
            <div className="space-y-3">
              {ads.ads.map((a, i) => (
                <div key={a.id || a.business_id || `ad-${i}`} className="relative rounded-2xl p-5 bg-slate-900/70 border-2 border-amber-400/70 shadow-[0_0_28px_rgba(251,191,36,0.32)]">
                  {a.discount && (
                    <div className="absolute -top-3 -right-2 w-16 h-16 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-slate-900 flex flex-col items-center justify-center font-heading font-black leading-none text-center shadow-lg rotate-6">
                      <span className="text-[15px]">{String(a.discount).split(" ")[0]}</span>
                      <span className="text-[9px] tracking-wide">{String(a.discount).split(" ").slice(1).join(" ") || "DEAL"}</span>
                    </div>
                  )}
                  <p className="font-heading font-extrabold text-xl leading-tight pr-14 text-white">{a.title}</p>
                  {a.detail && <p className="text-slate-300 text-sm mt-1">{a.detail}</p>}
                  <p className="text-[11px] mt-2 flex items-center gap-1 text-amber-300 font-semibold"><Clock className="w-3 h-3" />{a.expires}</p>
                  {a.live && a.id && (
                    <button
                      data-testid={`redeem-deal-${a.id}`}
                      onClick={() => startRedeem(a)}
                      disabled={redeemBusy}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-10 px-3.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-heading font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
                    >
                      {redeemBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgePercent className="w-4 h-4" />} Redeem exclusive · AIR.POG
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => { const b = ads.business; onClose(); if (b) routeTo(b); else toast("Can't route — this spot has no map pin yet 📍"); }}
                className="w-full h-14 rounded-2xl bg-cyan-400 text-slate-900 font-heading font-extrabold text-lg flex items-center justify-center gap-3 hover:bg-cyan-300 transition-colors mt-2 shadow-[0_0_24px_rgba(34,211,238,0.4)]"
              >
                <span className="w-9 h-9 rounded-full bg-slate-900/20 flex items-center justify-center"><ArrowRight className="w-5 h-5" /></span> POG IT!
              </button>
            </div>
          ) : spotlight ? (
            <div className="space-y-3" data-testid="spotlight-offer-card">
              <div className={`relative rounded-2xl p-5 bg-slate-900/70 border-2 ${spotlight.offer?.kind === "public_promo" ? "border-amber-400/70 shadow-[0_0_28px_rgba(251,191,36,0.32)]" : "border-sky-400/70 shadow-[0_0_28px_rgba(56,189,248,0.28)]"}`}>
                <div className={`absolute -top-3 -right-2 w-16 h-16 rounded-full bg-gradient-to-br ${spotlight.offer?.kind === "public_promo" ? "from-amber-300 to-amber-500" : "from-sky-300 to-sky-500"} text-slate-900 flex flex-col items-center justify-center font-heading font-black leading-none text-center shadow-lg rotate-6`}>
                  <span className="text-[20px]">{spotlight.offer?.kind === "public_promo" ? "🕵️" : "🎈"}</span>
                  <span className="text-[8px] tracking-wide">{spotlight.offer?.kind === "public_promo" ? "SPOTTED" : "PERK"}</span>
                </div>
                <p className={`text-[10px] font-black tracking-[0.2em] uppercase ${spotlight.offer?.kind === "public_promo" ? "text-amber-300" : "text-sky-300"}`}>{spotlight.offer?.kind === "public_promo" ? "POG Spotlight · Real Deal Spotted" : "POG Perk · Check-in Bonus"}</p>
                {spotlight.offer ? (
                  <>
                    <p data-testid="spotlight-offer-title" className="font-heading font-extrabold text-2xl leading-tight pr-14 text-amber-300 mt-1">{spotlight.offer.title}</p>
                    <p className="text-white text-sm font-semibold mt-1.5">{spotlight.offer.detail}</p>
                    {spotlight.offer.kind === "public_promo" && spotlight.offer.source_url && (
                      <a href={spotlight.offer.source_url} target="_blank" rel="noreferrer" className="text-xs text-sky-300 underline mt-1.5 block">View the merchant's announcement ↗</a>
                    )}
                    {spotlight.offer.kind === "public_promo" && (
                      <p className="text-[10px] text-slate-500 mt-1.5">🕵️ Spotted by {spotlight.spotter_name || "a POG'R"} · AIR.POG is not affiliated with this merchant; offer terms may change.</p>
                    )}
                    {spotlight.offer.kind === "public_promo" && <DealVotes spot={spotlight} driver={driver} />}
                    <p className="text-slate-400 text-xs mt-2">{spotlight.blurb || `${ads.business_name} — spotted by AIR.POG!`}</p>
                  </>
                ) : (
                  <p className="font-heading font-extrabold text-xl leading-tight pr-14 text-white mt-1">{spotlight.blurb || `${ads.business_name} — spotted by AIR.POG!`}</p>
                )}
                {spotlight.offer?.kind !== "public_promo" && (
                  <p className="text-slate-300 text-sm mt-2">Paid by AIR.POG — no purchase needed. Route there, check in & it's yours. Merchant deals drop here the day they claim! 👇</p>
                )}
                <p className="text-[11px] mt-2 flex items-center gap-1 text-amber-300 font-semibold"><Clock className="w-3 h-3" />Spotlight active — deals from this merchant may drop any day</p>
              </div>
              <button
                data-testid="spotlight-pog-it"
                onClick={() => { const b = ads.business; onClose(); if (b) routeTo(b); else toast("Can't route — this spot has no map pin yet 📍"); }}
                className="w-full h-14 rounded-2xl bg-cyan-400 text-slate-900 font-heading font-extrabold text-lg flex items-center justify-center gap-3 hover:bg-cyan-300 transition-colors shadow-[0_0_24px_rgba(34,211,238,0.4)]"
              >
                <span className="w-9 h-9 rounded-full bg-slate-900/20 flex items-center justify-center"><ArrowRight className="w-5 h-5" /></span> POG IT!
              </button>
              <DealSpotForm businessId={ads.business_id} driver={driver} biz={ads.business} />
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="bg-slate-800/60 rounded-2xl w-14 h-14 mx-auto flex items-center justify-center mb-3">
                <Tag className="w-6 h-6 text-slate-500" />
              </div>
              <p className="font-heading font-semibold mb-1">No active offers yet</p>
              <p className="text-slate-400 text-sm mb-2">{ads.note || "This business hasn't posted any deals."}</p>
              <div className="text-left mb-3"><DealSpotForm businessId={ads.business_id} driver={driver} biz={ads.business} /></div>
              <button
                onClick={() => { const b = ads.business; onClose(); if (b) routeTo(b); else toast("Can't route — this spot has no map pin yet 📍"); }}
                className="w-full h-14 rounded-2xl bg-cyan-400 text-slate-900 font-heading font-extrabold text-lg flex items-center justify-center gap-3 hover:bg-cyan-300 transition-colors shadow-[0_0_24px_rgba(34,211,238,0.4)]"
              >
                <span className="w-9 h-9 rounded-full bg-slate-900/20 flex items-center justify-center"><ArrowRight className="w-5 h-5" /></span> POG IT!
              </button>
            </div>
          )}
          {ads.business_id && <DealStackCarousel businessId={ads.business_id} />}
          {ads.business_id && <PogPingRow ads={ads} driver={driver} />}
          {ads.business && !ads.business.is_sale && (
            <div className="mt-4 pt-4 border-t border-slate-700/50 space-y-2">
              <button
                data-testid="checkin-btn"
                onClick={() => checkIn(ads.business)}
                disabled={checkinBusy}
                className="w-full rounded-2xl bg-gradient-to-r from-amber-400/20 to-orange-500/20 border border-amber-300/40 hover:from-amber-400/30 hover:to-orange-500/30 transition-colors px-4 py-3 flex items-center gap-3 text-left disabled:opacity-60"
              >
                <MapPin className="w-5 h-5 text-amber-300 shrink-0" />
                <span className="flex-1">
                  <span className="block font-heading font-bold text-sm text-amber-100">Check in here · +30 🎯 POG BUCKS</span>
                  <span className="block text-[11px] text-slate-400">{driver ? "Tap when you're at the spot to bank POG BUCKS." : "Log in to earn POG BUCKS for visiting."}</span>
                </span>
                {checkinBusy ? <Loader2 className="w-4 h-4 animate-spin text-amber-300" /> : <ArrowRight className="w-4 h-4 text-amber-300 shrink-0" />}
              </button>
              {checkinMsg && (
                <div data-testid="checkin-msg" className="text-center">
                  <p className={`text-xs font-bold ${checkinMsg.ok ? "text-emerald-300" : checkinMsg.err ? "text-rose-300" : "text-amber-200"}`}>
                    {checkinMsg.text || checkinMsg.err}
                  </p>
                  {checkinMsg.ok && checkinMsg.sub && (
                    <p data-testid="checkin-stack-note" className="text-[11px] font-bold text-emerald-200/80 mt-0.5">{checkinMsg.sub}</p>
                  )}
                </div>
              )}
              {(() => {
                const arrived = !!(driver && userPos && ads.business?.lat && ads.business?.lng && distMiles(userPos, ads.business) <= 0.12);
                return (
                  <button
                    data-testid="show-visit-qr-btn"
                    onClick={() => showVisitQr(ads.business)}
                    disabled={visitQrBusy}
                    className={`w-full rounded-2xl transition-colors px-4 py-3 flex items-center gap-3 text-left disabled:opacity-60 ${arrived ? "bg-gradient-to-r from-amber-400 to-yellow-500 border-2 border-amber-200 shadow-[0_0_26px_rgba(251,191,36,0.55)] animate-pulse" : "bg-gradient-to-r from-fuchsia-500/20 to-violet-500/20 border border-fuchsia-300/40 hover:from-fuchsia-500/30 hover:to-violet-500/30"}`}
                  >
                    <Coins className={`w-5 h-5 shrink-0 ${arrived ? "text-slate-900" : "text-fuchsia-300"}`} />
                    <span className="flex-1">
                      <span className={`block font-heading font-bold text-sm ${arrived ? "text-slate-900" : "text-fuchsia-100"}`}>
                        {arrived ? "🎉 You've arrived! Show your POG code" : "Show your POG code · earn +30 🪙 POG BUCKZ"}
                      </span>
                      <span className={`block text-[11px] ${arrived ? "text-slate-800/80" : "text-slate-400"}`}>
                        {arrived ? "Let the shop scan it to bank your +30 POG BUCKZ!" : (driver ? "Show this to the shop — they scan it to bank your POG BUCKZ." : "Log in free to earn & track your POG BUCKZ.")}
                      </span>
                    </span>
                    {visitQrBusy ? <Loader2 className={`w-4 h-4 animate-spin ${arrived ? "text-slate-900" : "text-fuchsia-300"}`} /> : <ArrowRight className={`w-4 h-4 shrink-0 ${arrived ? "text-slate-900" : "text-fuchsia-300"}`} />}
                  </button>
                );
              })()}
              <button
                data-testid="claim-business-cta"
                onClick={() => navigate(`/business?claim=${encodeURIComponent(ads.business.id)}&name=${encodeURIComponent(ads.business_name || "")}&lat=${ads.business.lat ?? ""}&lng=${ads.business.lng ?? ""}`)}
                className="w-full rounded-2xl bg-gradient-to-r from-emerald-500/15 to-sky-500/15 border border-emerald-400/30 hover:from-emerald-500/25 hover:to-sky-500/25 transition-colors px-4 py-3 flex items-center gap-3 text-left"
              >
                <Store className="w-5 h-5 text-emerald-400 shrink-0" />
                <span className="flex-1">
                  <span className="block font-heading font-bold text-sm text-emerald-100">Own this business? Claim it free</span>
                  <span className="block text-[11px] text-slate-400">Add your logo, post deals & reach drivers passing by.</span>
                </span>
                <ArrowRight className="w-4 h-4 text-emerald-300 shrink-0" />
              </button>
              <button
                data-testid="invite-business-btn"
                onClick={() => inviteBusiness(ads.business, ads.business_name)}
                className="w-full rounded-2xl bg-slate-800/60 hover:bg-slate-700/60 transition-colors px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-semibold text-slate-200"
              >
                <Share2 className="w-4 h-4 text-sky-400" /> Invite this business to AIR.POG
              </button>
              <button
                data-testid="print-card-btn"
                onClick={() => navigate(`/recruit?id=${encodeURIComponent(ads.business.id)}&name=${encodeURIComponent(ads.business_name || "")}&lat=${ads.business.lat ?? ""}&lng=${ads.business.lng ?? ""}`)}
                className="w-full text-center text-xs text-slate-400 hover:text-slate-200 transition-colors py-1"
              >
                📇 Print a Claim Card for this shop →
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
  );
};
